import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, setActiveUser } from '../lib/api';
import {
  loadAuthConfig,
  setSessionToken,
  signOut,
  consumeSsoTokenFromHash,
  type AuthConfig,
} from '../lib/auth';
import { LoginPage } from './LoginPage';
import { Skeleton } from './ui';

// Friendly text for the `?sso_error=` codes the SSO callback may redirect with.
const SSO_ERRORS: Record<string, string> = {
  sso_unavailable: 'Microsoft sign-in is not fully configured. Contact an administrator.',
  sso_denied: 'Microsoft sign-in was cancelled or denied.',
  sso_expired: 'That sign-in attempt expired. Please try again.',
  not_invited: 'Your Microsoft account is not provisioned for Tracx. Ask an administrator for access.',
  user_disabled: 'This account has been deactivated.',
  not_allowed: 'This account is not permitted to sign in.',
};
function humanizeSsoError(code: string): string {
  return SSO_ERRORS[code] ?? 'Microsoft sign-in failed. Please try again.';
}

type Phase = 'loading' | 'authed' | 'unauthed' | 'not_provisioned';
interface MeResponse {
  user: { id: string; email: string; name: string; role: string };
}

// Guards the single-use magic-link exchange against React StrictMode's
// double-invoked effect in dev (which would replay the token and 401 the
// second call). Module-level so it survives the remount.
let verifyHandled = false;

/**
 * Gates the app behind authentication.
 * - dev mode: short-circuits to authed (the X-Atlas-User persona resolves on the
 *   server), so local development is unchanged — no login screen.
 * - entra mode: initializes MSAL, completes any magic-link verify, then asks
 *   /auth/me. Renders the login screen when signed out and a clear message when
 *   the identity is valid but not provisioned (invite-only).
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [cfg, setCfg] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function boot() {
    const config = await loadAuthConfig();
    setCfg(config);

    // Complete a Microsoft SSO sign-in: the callback redirected us back with the
    // session token in the URL fragment. Store it and continue as authenticated.
    const ssoToken = consumeSsoTokenFromHash();
    if (ssoToken) setSessionToken(ssoToken);

    // Surface a failed SSO attempt (?sso_error=…) on the login screen.
    const url = new URL(window.location.href);
    const ssoError = url.searchParams.get('sso_error');
    if (ssoError) {
      setError(humanizeSsoError(ssoError));
      url.searchParams.delete('sso_error');
      window.history.replaceState({}, '', url.pathname + url.search);
    }

    // Complete a magic-link sign-in if we landed on /auth/verify?token=…
    if (url.pathname === '/auth/verify' && !verifyHandled) {
      verifyHandled = true;
      const token = url.searchParams.get('token');
      if (token) {
        try {
          const res = await api.post<{ token: string }>('/v2/auth/email/verify', { token });
          setSessionToken(res.token);
          window.location.replace('/'); // reload clean at root with the session set
          return;
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'This sign-in link is invalid or expired.');
        }
      }
      window.history.replaceState({}, '', '/');
    }

    if (config.mode === 'dev') {
      setPhase('authed'); // dev mode — persona resolves server-side
      return;
    }
    await resolveIdentity(); // email + sso modes require a real identity
  }

  async function resolveIdentity() {
    try {
      const me = await api.get<MeResponse>('/v2/auth/me');
      setActiveUser(me.user.email); // keep the active-user email in sync
      setPhase('authed');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(err.message);
        setPhase('not_provisioned');
      } else {
        setPhase('unauthed');
      }
    }
  }

  if (phase === 'loading') {
    return (
      <div className="min-h-screen grid place-items-center bg-surface">
        <Skeleton className="h-24 w-64" />
      </div>
    );
  }

  if (phase === 'authed') return <>{children}</>;

  if (phase === 'not_provisioned') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-surface">
        <div className="card w-full max-w-sm p-6 space-y-4 text-center">
          <h1 className="text-lg font-semibold">Access not granted</h1>
          <p className="text-sm text-muted">{error ?? 'Your account is not provisioned for Tracx.'}</p>
          <button className="btn-ghost border border-border w-full justify-center" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <LoginPage cfg={cfg!} initialError={error} />;
}
